import { defineMessages, MessageDescriptor } from 'react-intl';
import type { MemberExtractionResourceRow } from './useMemberExtractionDocuments';

const messages = defineMessages({
  incorrectValue: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.incorrectValue',
    defaultMessage: 'Incorrect value',
  },
  incorrectLoinc: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.incorrectLoinc',
    defaultMessage: 'Incorrect LOINC code',
  },
  incorrectCode: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.incorrectCode',
    defaultMessage: 'Incorrect code',
  },
  incorrectDate: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.incorrectDate',
    defaultMessage: 'Incorrect date',
  },
  notInDocument: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.notInDocument',
    defaultMessage: 'Not in the document',
  },
  duplicate: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.duplicate',
    defaultMessage: 'Duplicate',
  },
  notRelevant: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.notRelevant',
    defaultMessage: 'Not relevant or not needed',
  },
  other: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.other',
    defaultMessage: 'Other',
  },
  correctValue: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.correctValue',
    defaultMessage: 'Correct value',
  },
  correctLoinc: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.correctLoinc',
    defaultMessage: 'Correct LOINC code',
  },
  correctCode: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.correctCode',
    defaultMessage: 'Correct code or name',
  },
  correctDate: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.correctDate',
    defaultMessage: 'Correct date',
  },
  describeIssue: {
    id: 'phc-web-toolkit.memberExtractions.rejectReason.describeIssue',
    defaultMessage: 'Describe the issue',
  },
});

export type MemberExtractionRejectionReasonId =
  | 'incorrect_value'
  | 'incorrect_code'
  | 'incorrect_date'
  | 'not_in_document'
  | 'duplicate'
  | 'not_relevant'
  | 'other';

export type MemberExtractionRejectionDetail = {
  kind: 'text' | 'date' | 'multiline';
  label: MessageDescriptor;
  required: boolean;
  field: 'corrected_value' | 'rejection_reason';
};

export type MemberExtractionRejectionReason = {
  id: MemberExtractionRejectionReasonId;
  label: MessageDescriptor;
  detail?: MemberExtractionRejectionDetail;
};

export const getRejectionReasons = (
  resource: MemberExtractionResourceRow,
): MemberExtractionRejectionReason[] => {
  const isLab = resource.resourceType === 'Observation';
  const reasons: Array<MemberExtractionRejectionReason | false> = [
    Boolean(resource.value) && {
      id: 'incorrect_value',
      label: messages.incorrectValue,
      detail: {
        kind: 'text',
        label: messages.correctValue,
        required: true,
        field: 'corrected_value',
      },
    },
    {
      id: 'incorrect_code',
      label: isLab ? messages.incorrectLoinc : messages.incorrectCode,
      detail: {
        kind: 'text',
        label: isLab ? messages.correctLoinc : messages.correctCode,
        required: false,
        field: 'corrected_value',
      },
    },
    Boolean(resource.collectedAt) && {
      id: 'incorrect_date',
      label: messages.incorrectDate,
      detail: {
        kind: 'date',
        label: messages.correctDate,
        required: false,
        field: 'corrected_value',
      },
    },
    { id: 'not_in_document', label: messages.notInDocument },
    { id: 'duplicate', label: messages.duplicate },
    { id: 'not_relevant', label: messages.notRelevant },
    {
      id: 'other',
      label: messages.other,
      detail: {
        kind: 'multiline',
        label: messages.describeIssue,
        required: true,
        field: 'rejection_reason',
      },
    },
  ];
  return reasons.filter((reason): reason is MemberExtractionRejectionReason =>
    Boolean(reason),
  );
};

export const isRejectionComplete = (
  reason: MemberExtractionRejectionReason | undefined,
  detail: string,
) => Boolean(reason && (!reason.detail?.required || detail.trim()));

const SOURCE_RESOURCE_TYPES = [
  'DocumentReference',
  'Communication',
  'QuestionnaireResponse',
] as const;

type RejectionSourceResourceType = (typeof SOURCE_RESOURCE_TYPES)[number];

export const parseRejectionSource = (sourceReference: string) => {
  const [type, id, ...rest] = sourceReference.split('/');
  if (
    !id ||
    rest.length ||
    !SOURCE_RESOURCE_TYPES.includes(type as RejectionSourceResourceType)
  ) {
    return undefined;
  }
  return {
    source_resource_type: type as RejectionSourceResourceType,
    source_resource_id: id,
  };
};

// The reason is stored as its English label so it reads the same in the
// record no matter which locale the reviewer was using. Only
// `rejection_reason` and `corrected_value` reach the re-extraction prompt,
// so free-text detail is folded into one of them rather than `comment`.
export const buildRejectionFeedback = (
  reason: MemberExtractionRejectionReason,
  detail: string,
) => {
  const label = reason.label.defaultMessage as string;
  const text = detail.trim();
  if (!reason.detail || !text) {
    return { rejection_reason: label };
  }
  if (reason.detail.field === 'rejection_reason') {
    return { rejection_reason: `${label}: ${text}` };
  }
  return { rejection_reason: label, corrected_value: text };
};
